"use client";

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

interface BgmTrack {
  fileId: string;
  fileName: string;
  status: string;
}

interface BgmContextValue {
  muted: boolean;
  volume: number;
  setMuted: (v: boolean) => void;
  setVolume: (v: number) => void;
}

const BgmContext = createContext<BgmContextValue>({
  muted: false,
  volume: 0.5,
  setMuted: () => {},
  setVolume: () => {},
});

export function useBgm() {
  return useContext(BgmContext);
}

export function BgmProvider({ children }: { children: React.ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const tracksRef = useRef<BgmTrack[]>([]);
  const indexRef = useRef(0);
  const [muted, setMutedState] = useState(false);
  const [volume, setVolumeState] = useState(0.5);

  // localStorage から設定を復元
  useEffect(() => {
    const savedMuted = localStorage.getItem("bgm_muted") === "true";
    const savedVolume = parseFloat(localStorage.getItem("bgm_volume") ?? "0.5");
    setMutedState(savedMuted);
    setVolumeState(isNaN(savedVolume) ? 0.5 : savedVolume);
  }, []);

  // Audio要素の初期化 + トラック読み込み
  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;

    // 保存済み設定を即時反映
    audio.volume = parseFloat(localStorage.getItem("bgm_volume") ?? "0.5");
    audio.muted = localStorage.getItem("bgm_muted") === "true";

    // トラック終了 → 次のトラックへ
    const onEnded = () => {
      if (tracksRef.current.length === 0) return;
      indexRef.current = (indexRef.current + 1) % tracksRef.current.length;
      audio.src = `/api/bgm/${tracksRef.current[indexRef.current].fileId}`;
      audio.play().catch(() => {});
    };
    audio.addEventListener("ended", onEnded);

    // BGMリスト取得
    fetch("/api/bgm/list")
      .then((r) => r.json())
      .then((tracks: BgmTrack[]) => {
        tracksRef.current = tracks;
        if (tracks.length > 0) {
          audio.src = `/api/bgm/${tracks[0].fileId}`;
        }
      })
      .catch(() => {});

    // 最初のユーザー操作で再生開始（ブラウザのAutoplay Policy対策）
    let interacted = false;
    const onInteract = () => {
      if (interacted) return;
      interacted = true;
      if (audio.src && !audio.muted) {
        audio.play().catch(() => {});
      }
    };
    window.addEventListener("click", onInteract);
    window.addEventListener("touchstart", onInteract);

    return () => {
      audio.removeEventListener("ended", onEnded);
      window.removeEventListener("click", onInteract);
      window.removeEventListener("touchstart", onInteract);
      audio.pause();
      audio.src = "";
    };
  }, []);

  const setMuted = useCallback((v: boolean) => {
    setMutedState(v);
    localStorage.setItem("bgm_muted", String(v));
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = v;
    // ミュート解除時に停止中なら再生再開
    if (!v && audio.src && audio.paused) {
      audio.play().catch(() => {});
    }
  }, []);

  const setVolume = useCallback((v: number) => {
    setVolumeState(v);
    localStorage.setItem("bgm_volume", String(v));
    if (audioRef.current) audioRef.current.volume = v;
  }, []);

  return (
    <BgmContext.Provider value={{ muted, volume, setMuted, setVolume }}>
      {children}
    </BgmContext.Provider>
  );
}
